import {
  CSSProperties,
  MutableRefObject,
  memo,
  useCallback,
  useEffect, useRef,
  useState
} from "react";
import { DragResult, ElementRole } from "./dnd-internal";
import styles from "./index.module.scss";
import cn from "classnames";
import { addSideStyle, noSelect } from "./styles";
import * as typ from "./types";
import Item from "./item";
import { dev } from "@utils/dev";

export const ITEM_PADDING_COLS = 1;

export interface ScrollInfo {
  needScroll?: boolean;
  target?: HTMLDivElement;
  row?: number;
  id?: string;
}

export interface RowProps {
  row: typ.Row;
  columnWidth: number;
  rowIndex: number;
  dragType: string;
  scroll: ScrollInfo;
  scrollX: number;
  groupWidth: number;

  dragged?: DragResult;
  extDragged?: DragResult;
  dragPos?: [number, number];
  usingOverInfo?: boolean;
  className?: string;
  style?: CSSProperties;
  unlocked?: boolean;

  dragStart(
    ev: typ.ItemMouseEvent,
    el: MutableRefObject<typ.HTMLItemElement | null>,
    item: typ.Item
  ): void;
  itemExtDragOver(ev: typ.ItemDragEvent, item: typ.Item): void;
  registerRowWidth(w: number): void;
}

interface ItemInfo {
  data: typ.Item;
  style?: CSSProperties;
  virtual?: boolean;
}

const debugPrintRowItems = (items: ItemInfo[]) => {
  dev.llog("\nROW START");
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    dev.llog(
      `  ITEM id:${item.data.id},col:${item.data.pos.col},width:${item.data.numCols},pos:${i}`
    );
  }
  dev.llog("ROW END");
};

const useRowControl = (props: RowProps) => {
  const [items, setItems] = useState<ItemInfo[]>();
  const self = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (self.current) {
      const el = self.current;
      props.row.rect = () => el.getBoundingClientRect();
    }
  }, [props.row, self.current]);

  useEffect(() => {
    if (
      self.current &&
      props.scroll.needScroll &&
      props.scroll.row === props.rowIndex
    ) {
      const rect = self.current.getBoundingClientRect();
      if (rect.top >= window.innerHeight) {
        window.scrollTo({
          top: rect.bottom - window.innerHeight,
          behavior: "smooth",
        });
      }
    }
  }, [self.current, props.scroll]);

  const itemStyle = useCallback(
    (item: typ.Item, origStyle?: CSSProperties): CSSProperties | undefined => {
      let st = { ...origStyle };
      if (props.dragged) {
        st = { ...st, ...noSelect };
      }
      if (item.id === props.dragged?.dragged.id && props.dragPos) {
        const [x, y] = props.dragPos;
        return {
          ...st,
          transform: `translate(${Math.round(
            x
          )}px, ${Math.round(y)}px)`,
          opacity: 0.5,
          pointerEvents: "none",
          zIndex: 100,
        };
      }
      if (item.id === props.dragged?.over.id)
        return addSideStyle(st, props.dragged?.side);
      if (item.id === props.extDragged?.over.id)
        return addSideStyle(st, props.extDragged?.side);
      return st;
    },
    [props.dragged, props.dragPos, props.extDragged]
  );

  useEffect(() => {
    const r = props.row;
    const d = props.dragged || props.extDragged;
    const newItems: ItemInfo[] = [];
    let prevCol = 0;
    const numCols = Math.max(
      (self.current?.getBoundingClientRect().width || 0) /
        (props.columnWidth || 1),
      r.items.length
    );

    const tab: Record<string, ItemInfo> = {};
    const addItem = (it: ItemInfo) => {
      if (tab.hasOwnProperty(it.data.id)) {
        dev.err("ITEM REPEAT", it.data);
      } else {
        tab[it.data.id] = it;
        newItems.push(it);
      }
    };

    let width = 0;
    for (let col = 0; col < numCols; col++) {
      const item = r.items[col];
      const offset = props.columnWidth * (col - prevCol);

      if (item) {
        const newItem: ItemInfo = {
          data: item,
          style: {
            ...itemStyle(item),
            marginLeft: `${offset}px`,
            padding: `0 ${Math.round(
              (ITEM_PADDING_COLS * props.columnWidth) / 2
            )}px`,
          },
        };
        width += offset + item.numCols * props.columnWidth;
        addItem(newItem);
        prevCol = col + item.numCols;
      }

      props.registerRowWidth(width);

      if (
        d?.over.pos.row === r.offset &&
        d?.over.pos.col === col &&
        d.over.role >= ElementRole.virtual
      ) {
        const newItem: ItemInfo = {
          data: {
            ...d.over,
            numCols: d.over.numCols,
            render: <></>,
            userInfo: undefined,
          },
          virtual: true,
          style: {
            left: `${props.columnWidth * d.over.pos.col}px`,
            width: `${d.over.numCols * props.columnWidth}px`,
            padding: `0 ${Math.round(
              (ITEM_PADDING_COLS * props.columnWidth) / 2
            )}px`,
          },
        };
        addItem(newItem);
      }
    }
    setItems(newItems);
  }, [props.row, props.dragged, props.extDragged, props.dragPos, self.current]);

  return {
    self,
    items,
    isDrag: !!props.dragged,
    style: {
      minHeight: `${maxHeight + 40}px`,
      minWidth: `${props.groupWidth}px`,
    } as CSSProperties,

    dragStart: props.dragStart,
    itemExtDragOver: props.itemExtDragOver,
    setHeight(h: number) {
      setMaxHeight((hh) => (h > hh ? h : hh));
    },
  };
};

const RowComponent = (props: RowProps) => {
  const ctrl = useRowControl(props);

  return (
    <div
      className={cn(props.className, styles.row)}
      style={{ ...props.style, ...ctrl.style }}
      ref={ctrl.self}
    >
      {ctrl.items?.map((item) =>
        item.virtual ? (
          <div
            key={item.data.id}
            className={styles.virtual}
            style={item.style}
            data-id={item.data.id}
          ></div>
        ) : (
          <Item
            key={`${item.data.id} ${item.data.numCols}`}
            style={item.style}
            control={ctrl}
            unlocked={props.unlocked}
            item={item.data}
            dragType={props.dragType}
            columnWidth={props.columnWidth}
            shift={
              item.data.id === props.dragged?.over.id ||
              item.data.id === props.extDragged?.over.id
            }
            scroll={props.scroll}
            scrollX={props.scrollX}
            setHeight={ctrl.setHeight}
          />
        )
      )}
    </div>
  );
};

export default memo(RowComponent);
